import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import axios from "axios";
import Seo from "@/components/Seo";
import { Reveal, FadeIn } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="blog-post-not-found">
        <h1 className="font-serif text-4xl text-ink">Articolo non trovato</h1>
        <Link
          to="/blog"
          className="mt-6 inline-block text-gold font-semibold link-underline"
          data-testid="blog-post-back-notfound"
        >
          Torna al blog
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-40 pb-32 text-center text-stone" data-testid="blog-post-loading">
        Caricamento…
      </div>
    );
  }

  return (
    <article data-testid="blog-post-page">
      <Seo
        title={post.meta_title}
        description={post.meta_description}
        image={post.cover_image && post.cover_image.startsWith("http") ? post.cover_image : undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt,
          inLanguage: "it-IT",
          author: { "@type": "Person", name: "Laure Hila" },
        }}
      />

      <header className="pt-32 md:pt-44 pb-10 md:pb-14 grain relative">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <FadeIn>
            <Link
              to="/blog"
              data-testid="blog-post-back"
              className="inline-flex items-center gap-2 text-sm font-semibold text-stone hover:text-gold transition-colors duration-300"
            >
              <ArrowLeft size={16} />
              Tutti gli articoli
            </Link>
            <div className="mt-8 flex items-center gap-3 text-xs uppercase tracking-[0.18em]">
              <span className="text-gold font-semibold">{post.category}</span>
            </div>
            <h1 className="mt-5 font-serif text-4xl md:text-5xl font-medium leading-tight tracking-tight text-ink text-balance">
              {post.title}
            </h1>
          </FadeIn>
        </div>
      </header>

      <Reveal className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="overflow-hidden rounded-2xl">
          <img
            src={post.cover_image}
            alt={post.cover_alt}
            style={{ objectPosition: post.cover_position || "center" }}
            className="w-full aspect-[16/9] object-cover"
            loading="eager"
          />
        </div>
      </Reveal>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-14 md:py-20">
        {post.content.map((block, i) => {
          if (block.type === "h2") {
            return (
              <Reveal key={i}>
                <h2 className="font-serif text-3xl md:text-4xl font-medium text-ink mt-12 mb-5 tracking-tight">
                  {block.text}
                </h2>
              </Reveal>
            );
          }
          if (block.type === "quote") {
            return (
              <Reveal key={i}>
                <blockquote className="my-12 pl-6 border-l-2 border-gold font-serif text-2xl md:text-3xl italic leading-snug text-ink">
                  «{block.text}»
                </blockquote>
              </Reveal>
            );
          }
          return (
            <Reveal key={i}>
              <p className="text-base md:text-lg leading-relaxed text-stone mb-6">
                {block.text}
              </p>
            </Reveal>
          );
        })}

        <Reveal>
          <div className="mt-16 pt-10 border-t border-line flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <p className="font-serif text-2xl text-ink">Laure Hila</p>
              <p className="text-sm text-stone mt-1">
                Viaggi • Cambiamento • Libertà
              </p>
            </div>
            <Link
              to="/contatti"
              data-testid="blog-post-cta-contatti"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-ink text-cream text-sm font-semibold hover:bg-gold transition-colors duration-300"
            >
              Parliamone
            </Link>
          </div>
        </Reveal>
      </div>
    </article>
  );
}
